import { ArrowLeft, Menu, type LucideIcon } from "lucide-react";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router";

import { Button, ButtonLink } from "@/components/ui";
import { usePlan } from "@/features/billing/plan";
import { PlanNoticeBar } from "@/features/billing/plan-notice";
import { useT } from "@/i18n/provider";
import { useShell } from "./shell-context";

/**
 * One signed-in page: its top bar, the notices under it, and its content
 * (guide 3.6, marks 2–5).
 *
 *   Computer/tablet: a white 72 px bar: ← (if any), the title, and the page's
 *   green button on the right (with a quieter second one beside it, rarely).
 *   Phone: the phone app's green 56 px bar with ☰ (← on inner pages) and the
 *   title; the main button runs across the width just above the tabs (the
 *   second one, when there is one, beside it).
 *
 * Every page sets the browser tab's name: "Sales · FarmashiBook".
 */

export interface PageAction {
  label: string;
  icon?: LucideIcon;
  /** Goes somewhere: + Add sale. */
  to?: string;
  /** Or does something: Save. */
  onClick?: () => void;
  loading?: boolean;
  disabled?: boolean;
  /**
   * It would record or change something: once the plan has ended (view only),
   * a click says so instead (features/billing/plan.tsx).
   */
  changes?: boolean;
}

export function Page({
  title,
  subtitle,
  back,
  action,
  secondaryAction,
  children,
}: {
  title: string;
  subtitle?: string;
  /** Where ← goes. Pages reached from a menu have none (☰ on the phone instead). */
  back?: string;
  action?: PageAction;
  /** A rarer second button beside the main one: Invite an owner next to Add staff. */
  secondaryAction?: PageAction;
  children?: React.ReactNode;
}) {
  const t = useT();
  const { openMenu } = useShell();

  useEffect(() => {
    document.title = title === t.appName ? t.appName : `${title} · ${t.appName}`;
  }, [title, t.appName]);

  return (
    <>
      {/* Phone: the green bar. */}
      <header className="sticky top-0 z-20 flex h-14 items-center gap-1 bg-header px-1 text-on-primary md:hidden">
        {back ? (
          <Link
            to={back}
            aria-label={t.back}
            className="flex size-12 items-center justify-center rounded-full hover:bg-on-primary/10 focus-visible:outline-on-primary"
          >
            <ArrowLeft size={24} aria-hidden="true" />
          </Link>
        ) : (
          <button
            type="button"
            onClick={openMenu}
            aria-label={t.menu}
            className="flex size-12 items-center justify-center rounded-full hover:bg-on-primary/10 focus-visible:outline-on-primary"
          >
            <Menu size={24} aria-hidden="true" />
          </button>
        )}
        <h1 className="min-w-0 flex-1 truncate text-heading font-bold">{title}</h1>
      </header>

      {/* Computer and tablet: the white bar. */}
      <header className="sticky top-0 z-10 hidden min-h-[72px] items-center gap-3 border-b border-border bg-surface px-6 md:flex lg:px-10">
        {back && (
          <Link
            to={back}
            aria-label={t.back}
            title={t.back}
            className="-ml-2 flex size-10 items-center justify-center rounded-full text-text hover:bg-primary-soft"
          >
            <ArrowLeft size={24} aria-hidden="true" />
          </Link>
        )}
        <div className="flex min-w-0 flex-1 flex-col py-2">
          <h1 className="text-title font-bold break-words text-text">{title}</h1>
          {subtitle && <p className="text-label break-words text-muted">{subtitle}</p>}
        </div>
        {secondaryAction && <ActionButton action={secondaryAction} secondary />}
        {action && <ActionButton action={action} />}
      </header>

      {/* The plan's notice, when there is something to say. */}
      <PlanNoticeBar />

      <main className={action ? "pb-20 md:pb-0" : undefined}>
        <div className="mx-auto flex max-w-[1200px] flex-col gap-4 px-4 py-4 md:px-6 md:py-6 lg:px-10">
          {subtitle && <p className="text-label text-muted md:hidden">{subtitle}</p>}
          {children}
        </div>
      </main>

      {/* Phone: the main action across the width, above the tabs. */}
      {action && (
        <div
          className={
            "fixed inset-x-0 bottom-16 z-20 border-t border-border bg-background p-3 md:hidden" +
            (secondaryAction ? " grid grid-cols-2 gap-2" : "")
          }
        >
          {secondaryAction && <ActionButton action={secondaryAction} secondary fullWidth />}
          <ActionButton action={action} fullWidth />
        </div>
      )}
    </>
  );
}

function ActionButton({
  action,
  fullWidth,
  secondary,
}: {
  action: PageAction;
  fullWidth?: boolean;
  secondary?: boolean;
}) {
  const variant = secondary ? "secondary" : undefined;
  const plan = usePlan();
  const navigate = useNavigate();
  if (action.changes && plan.viewOnly) {
    // A button, not a link: the click says "View only" and goes nowhere.
    const go = action.to;
    return (
      <Button
        label={action.label}
        icon={action.icon}
        variant={variant}
        disabled={action.disabled}
        fullWidth={fullWidth}
        onClick={plan.guard(() => (go ? void navigate(go) : action.onClick?.()))}
      />
    );
  }
  if (action.to && !action.disabled) {
    return (
      <ButtonLink to={action.to} label={action.label} icon={action.icon} variant={variant} fullWidth={fullWidth} />
    );
  }
  return (
    <Button
      label={action.label}
      icon={action.icon}
      variant={variant}
      onClick={action.onClick}
      loading={action.loading}
      disabled={action.disabled}
      fullWidth={fullWidth}
    />
  );
}
